import { useState, useEffect, createRef, PropsWithChildren, ReactNode } from 'react';
import CarouselItem from "MiniSiteComponents/Carousel/Carousel.interface"
import Consts from 'Interfaces/Consts';
import CustomLinkButton from "GeneralComponents/CustomLinkButton";
import Helper from 'Interfaces/Helper';

interface Props {
    contentShowItem: CarouselItem;
    componentId: string;
    componentTitle: string;
    collapseId: string;
    isSelected: boolean;
    closeCard: () => void;
    linkTarget?: string;
}

const CollapseContentCard = ({ contentShowItem, componentId, closeCard, isSelected, collapseId, componentTitle, linkTarget }: Props) => {

    const [isContentExpended, setIsContentExpended] = useState(false);
    const [hasLongContent, setHasLongContent] = useState(false);

    const contentBodyId = componentId + '-circleItem-content-' + contentShowItem.index;
    const dir = getComputedStyle(document.documentElement).direction;

    const onClose = () => {
        closeCard();
        setIsContentExpended(false);
    }
    const onShowMoreClick = (e: any) => {
        Helper.handleLinkClicksGA4('Circles View', componentTitle, e.target.innerText + "- more");
        setIsContentExpended(true);
    }

    // close expended-content on card-close
    useEffect(() => { setIsContentExpended(false); }, [isSelected]);


    return (
        <div id={collapseId} className={`collapse ${isSelected ? 'show collapsing' : 'collapsing'} mst-circlesItemContent`}
            aria-labelledby={componentId + "-heading-" + contentShowItem.index}
            data-parent={"#" + componentId}>

            <CardTriangleUp />

            <div className="card-body mst-circlesItem-body">

                <Header>

                    <CloseCardBtn collapseId={collapseId} isSelected={isSelected} onClose={onClose} />

                    <Title title={contentShowItem.title} />

                    <ShortDescription description={contentShowItem.richDescription} />

                    <hr />

                </Header>

                <LongContent
                    content={contentShowItem.content}
                    contentBodyId={contentBodyId}
                    isContentExpended={isContentExpended}
                    setHasLongContent={setHasLongContent}
                />

                <ShowMoreWrapper componentId={componentId} hasLongContent={hasLongContent}>
                    {
                        isContentExpended ?
                            <CloseContentBtn collapseId={collapseId} isSelected={isSelected} onClose={onClose} />
                            :
                            <ShowMoreBtn contentBodyId={contentBodyId} onClick={onShowMoreClick} isRtl={dir=='rtl' } />
                    }
                </ShowMoreWrapper>

                <CustomLinkButton
                    title={Consts.readMore}
                    pageUrl={contentShowItem.url}
                    link={contentShowItem.link}
                    isBtn={false}
                    btnClass={"btn mst-btn btn-primary px-auto txt-pallete-color"}
                    linkClass={"txt-pallete-color"}
                    dir={dir}
                    linkIcon={true}
                    charLimit={-3}
                    linkTarget={linkTarget}
                />
            </div>
        </div>
    )
}

const CardTriangleUp = () => <div className="mst-circlesItem-triangleUp primary-color mx-auto"></div>;

const Header = ({ children }: PropsWithChildren) => {
    return <div className="mst-circlesItem-contentHeader"> {children} </div>
}

const Title = ({ title }: { title: string }) => {
    return <div className={"mst-circlesItem-contentTitle me-4"}>{title}</div>
}

const ShortDescription = ({ description }: { description: string }) => {
    return <div className="mst-circlesItem-contentShortDesc me-4" dangerouslySetInnerHTML={{ __html: description }}></div>
}

const LongContent = ({ content, contentBodyId, isContentExpended, setHasLongContent }: { isContentExpended: boolean; contentBodyId: string; content: string; setHasLongContent: (isLong: boolean) => void; }) => {

    const contentBodyEl = createRef<HTMLDivElement>();
    // Check long content on Mount
    useEffect(() => {
        const isLongContent = contentBodyEl && contentBodyEl.current.clientHeight !== contentBodyEl.current.scrollHeight;
        setHasLongContent(isLongContent);
    }, []);
    return <div ref={contentBodyEl} className={`mst-circlesItem-contentBody collapse ${isContentExpended ? 'show' : ''}`}
        aria-expanded={isContentExpended} id={contentBodyId}>
        <div dangerouslySetInnerHTML={{ __html: content }}></div>
    </div>
}

const CloseCardBtn = ({ collapseId, isSelected, onClose }: { collapseId: string; onClose: () => void; isSelected: boolean; }) => {
    return <button className="mst-circlesItem-contentClose" type="button"
        data-bs-toggle="collapse" data-bs-target={"#" + collapseId}
        aria-expanded={isSelected} aria-controls={collapseId} onClick={onClose}>
        <i className="icon-minisite-close minisite-font-36" />
    </button>
}

const ShowMoreWrapper = ({ children, componentId, hasLongContent }: { componentId: string; hasLongContent: boolean; children: ReactNode }) => {
    return <div className={`${hasLongContent ? '' : 'd-none'} ${componentId + '-wrapper-readMore'}`}> {children} </div>
}

const ShowMoreBtn = ({ contentBodyId, onClick, isRtl }: { contentBodyId: string; onClick: (e: any) => void; isRtl: boolean; }) => {
    return <a role="button" className="mst-showMore txt-pallete-color"
        aria-expanded="false" aria-controls={contentBodyId}
        onClick={onClick}>
        {Consts.showMore}
        <i className={`fa fa-fw fa-chevron-${isRtl ? 'left' : 'right'}`} aria-hidden="true"></i>
    </a>
}

const CloseContentBtn = ({ collapseId, isSelected, onClose }: { collapseId: string; isSelected: boolean; onClose: () => void; }) => {
    return <a role="button" className="mst-showMore txt-pallete-color"
        aria-expanded={isSelected} aria-controls={collapseId}
        data-bs-toggle="collapse" data-bs-target={"#" + collapseId}
        onClick={onClose}>
        {Consts.close}
        <i className={`fa fa-fw fa-chevron-up`} aria-hidden="true"></i>
    </a>
}

export default CollapseContentCard;